import { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TopicCard as TopicGridCard } from "./TopicCard";
import { 
  Search, 
  BookOpen, 
  FileText, 
  Clock, 
  Target,
  TrendingUp,
  Star,
  Stethoscope,
  Heart,
  Brain,
  Eye,
  Activity,
  ChevronRight,
  Grid3X3,
  List,
  BarChart3,
  RotateCcw,
  AlertCircle,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useExamContext } from "@/contexts/ExamContext";
import { useAllUserSubscriptions } from "@/hooks/useAllUserSubscriptions";
import { ExamSwitcher } from "@/components/dashboard/ExamSwitcher";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Topic {
  id: string;
  name: string;
  parent_topic_id?: string | null;
}

interface TopicStats {
  mcqCount: number;
  notesCount: number;
  difficulty: { easy: number; medium: number; hard: number };
  estimatedTime: number;
  progress: number;
}

interface TopicHubProps {
  onTopicSelect?: (topicId: string, contentType: 'mcq' | 'notes' | 'mixed') => void;
  className?: string;
}

const getTopicIcon = (topicName: string) => {
  const lower = topicName.toLowerCase();
  if (lower.includes('cardio') || lower.includes('heart')) return Heart;
  if (lower.includes('neuro') || lower.includes('brain')) return Brain;
  if (lower.includes('ophthal') || lower.includes('eye')) return Eye;
  if (lower.includes('vital') || lower.includes('monitor')) return Activity;
  return Stethoscope;
};

const getSpecialtyColor = (topicName: string) => {
  const lower = topicName.toLowerCase();
  if (lower.includes('cardio') || lower.includes('heart')) return 'medical-orange';
  if (lower.includes('neuro') || lower.includes('brain')) return 'medical-purple';
  if (lower.includes('ophthal') || lower.includes('eye')) return 'medical-green';
  if (lower.includes('vital') || lower.includes('monitor')) return 'medical-blue';
  return 'medical-primary';
};

export const TopicHub = ({ onTopicSelect, className = '' }: TopicHubProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'content' | 'progress'>('name');
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [userIdReady, setUserIdReady] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resetTopicId, setResetTopicId] = useState<string | null>(null);
  const { selectedExamSlug, availableExams, isLoading: examContextLoading, isAutoSelecting } = useExamContext();

  // Get current user
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
      setUserIdReady(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      setUserIdReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Only invalidate queries when exam actually changes (not on every auto-selection)
  const prevExamRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (!isAutoSelecting && selectedExamSlug !== null && userIdReady) {
      if (prevExamRef.current !== selectedExamSlug) {
        console.log('[TopicHub] Exam changed, invalidating queries:', prevExamRef.current, '->', selectedExamSlug);
        queryClient.invalidateQueries({ queryKey: ["topic-mcq-stats-filtered"] });
        queryClient.invalidateQueries({ queryKey: ["topic-notes-stats-filtered"] });
        prevExamRef.current = selectedExamSlug;
      }
    }
  }, [isAutoSelecting, selectedExamSlug, userIdReady, queryClient]);

  // Fetch topics
  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topics")
        .select("id, name, parent_topic_id")
        .order("name", { ascending: true });
      if (error) throw error;
      return data as Topic[];
    },
  });

  // Fetch MCQ stats per topic - filtered by selected exam
  const { data: mcqStats = {}, isLoading: mcqStatsLoading, error: mcqStatsError } = useQuery({
    queryKey: ["topic-mcq-stats-filtered", selectedExamSlug, userId],
    enabled: userIdReady && !isAutoSelecting && !examContextLoading,
    queryFn: async () => {
      console.log('[TopicHub MCQ Query] === QUERY START ===');
      console.log('[TopicHub MCQ Query] State:', {
        selectedExamSlug,
        availableExamsCount: availableExams.length,
        examContextLoading,
        userId,
        isAutoSelecting
      });

      if (!userId) {
        console.log('[TopicHub MCQ Query] No user ID, returning empty stats');
        return {};
      }


      // Step 1: Get exam label IDs
      let labelIds: string[] = [];
      
      if (selectedExamSlug) {
        // @ts-ignore - Supabase types causing deep instantiation error
        const labelResult = await supabase
          .from("exam_labels")
          .select("id")
          .eq("slug", selectedExamSlug)
          .limit(1);
        
        const labelData = labelResult.data?.[0];
        const labelError = labelResult.error;

        if (labelError) {
          console.error('[TopicHub MCQ Query] Error fetching label for exam:', selectedExamSlug, labelError);
          throw labelError;
        }

        if (labelData?.id) {
          labelIds = [labelData.id];
          console.log(`[TopicHub MCQ Query] Found label ID for ${selectedExamSlug}:`, labelData.id);
        } else {
          console.log(`[TopicHub MCQ Query] No label found for exam: ${selectedExamSlug}`);
          return {};
        }
      } else {
        const examSlugs = availableExams.map(e => e.slug);
        
        if (examSlugs.length === 0) {
          console.log('[TopicHub MCQ Query] No available exams');
          return {};
        }

        const { data: labelData, error: labelError } = await supabase
          .from("exam_labels")
          .select("id")
          .in("slug", examSlugs);

        if (labelError) {
          console.error('[TopicHub MCQ Query] Error fetching labels for exams:', examSlugs, labelError);
          throw labelError;
        }

        labelIds = (labelData || []).map(l => l.id);
        console.log(`[TopicHub MCQ Query] Found ${labelIds.length} label IDs for ${examSlugs.length} exams`);
      }

      if (labelIds.length === 0) {
        console.log('[TopicHub MCQ Query] No label IDs found, returning empty stats');
        return {};
      }

      // Step 2: Use optimized aggregation function
      const { data: aggregatedData, error: aggregatedError } = await supabase
        .rpc('get_mcq_stats_by_topic', {
          p_label_ids: labelIds
        });

      if (aggregatedError) {
        console.error('[TopicHub MCQ Query] Aggregation error:', aggregatedError);
        throw aggregatedError;
      }

      console.log(`[TopicHub MCQ Query] Aggregated stats for ${aggregatedData?.length || 0} topics`);

      const stats: Record<string, { count: number; difficulty: { easy: number; medium: number; hard: number } }> = {};

      (aggregatedData || []).forEach((row: any) => {
        stats[row.topic_id] = {
          count: Number(row.count),
          difficulty: {
            easy: Number(row.easy) || 0,
            medium: Number(row.medium) || 0,
            hard: Number(row.hard) || 0
          }
        };
      });

      console.log('[TopicHub MCQ Query] Final stats:', stats);
      return stats;
    },
  });

  // Fetch Notes stats per topic - filtered by selected exam
  const { data: notesStats = {}, isLoading: notesStatsLoading, error: notesStatsError } = useQuery({
    queryKey: ["topic-notes-stats-filtered", selectedExamSlug, userId],
    enabled: userIdReady && !isAutoSelecting && !examContextLoading,
    queryFn: async () => {
      console.log('[TopicHub Notes Query] === QUERY START ===');
      console.log('[TopicHub Notes Query] State:', {
        selectedExamSlug,
        availableExamsCount: availableExams.length,
        userId
      });

      if (!userId) {
        console.log('[TopicHub Notes Query] No user ID, returning empty stats');
        return {};
      }

      // Step 1: Get exam label IDs
      let labelIds: string[] = [];
      
      if (selectedExamSlug) {
        const { data: labelData, error: labelError } = await supabase
          .from("exam_labels")
          .select("id")
          .eq("slug", selectedExamSlug)
          .maybeSingle();

        if (labelError) {
          console.error('[TopicHub Notes Query] Error fetching label for exam:', selectedExamSlug, labelError);
          throw labelError;
        }

        if (labelData?.id) {
          labelIds = [labelData.id];
          console.log(`[TopicHub Notes Query] Found label ID for ${selectedExamSlug}:`, labelData.id);
        } else {
          console.log(`[TopicHub Notes Query] No label found for exam: ${selectedExamSlug}`);
          return {};
        }
      } else {
        const examSlugs = availableExams.map(e => e.slug);
        
        if (examSlugs.length === 0) {
          console.log('[TopicHub Notes Query] No available exams');
          return {};
        }

        const { data: labelData, error: labelError } = await supabase
          .from("exam_labels")
          .select("id")
          .in("slug", examSlugs);

        if (labelError) {
          console.error('[TopicHub Notes Query] Error fetching labels for exams:', examSlugs, labelError);
          throw labelError;
        }

        labelIds = (labelData || []).map(l => l.id);
        console.log(`[TopicHub Notes Query] Found ${labelIds.length} label IDs for ${examSlugs.length} exams`);
      }

      if (labelIds.length === 0) {
        console.log('[TopicHub Notes Query] No label IDs found, returning empty stats');
        return {};
      }

      // Step 2: Use optimized aggregation function
      const { data: aggregatedData, error: aggregatedError } = await supabase
        .rpc('get_note_stats_by_topic', {
          p_label_ids: labelIds
        });

      if (aggregatedError) {
        console.error('[TopicHub Notes Query] Aggregation error:', aggregatedError);
        throw aggregatedError;
      }

      console.log(`[TopicHub Notes Query] Aggregated stats for ${aggregatedData?.length || 0} topics`);

      const stats: Record<string, { count: number; estimatedReadTime: number }> = {};

      (aggregatedData || []).forEach((row: any) => {
        stats[row.topic_id] = {
          count: Number(row.count),
          estimatedReadTime: Number(row.count) * 5
        };
      });

      console.log('[TopicHub Notes Query] Final stats:', stats);
      return stats;
    },
  });

  // Fetch user progress per topic
  const { data: userTopicProgress = {} } = useQuery({
    queryKey: ["user-topic-progress", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data: attempts, error } = await supabase
        .from("attempts")
        .select("mcq_id, mcqs!inner(topic_id)")
        .eq("user_id", userId);
      
      if (error) throw error;
      
      const topicAttempts: Record<string, Set<string>> = {};
      attempts?.forEach((attempt: any) => {
        const topicId = attempt.mcqs?.topic_id;
        if (!topicId) return;
        if (!topicAttempts[topicId]) {
          topicAttempts[topicId] = new Set();
        }
        topicAttempts[topicId].add(attempt.mcq_id);
      });
      
      return topicAttempts;
    }
  });

  // Process topics with stats
  const enrichedTopics = useMemo(() => {
    const processed = topics.map(topic => {
      const mcqData = mcqStats[topic.id] || { count: 0, difficulty: { easy: 0, medium: 0, hard: 0 } };
      const notesData = notesStats[topic.id] || { count: 0, estimatedReadTime: 0 };
      
      const attemptedCount = userId && userTopicProgress[topic.id] 
        ? userTopicProgress[topic.id].size 
        : 0;
      const totalCount = mcqData.count;
      const progress = totalCount > 0 ? Math.round((attemptedCount / totalCount) * 100) : 0;
      
      return {
        ...topic,
        stats: {
          mcqCount: mcqData.count,
          notesCount: notesData.count,
          difficulty: mcqData.difficulty,
          estimatedTime: notesData.estimatedReadTime,
          progress,
        } as TopicStats
      };
    });

    // Debug log sample topics
    console.log('[TopicHub] Enriched Topics Sample:', processed.slice(0, 3).map(t => ({
      id: t.id,
      name: t.name,
      mcqCount: t.stats.mcqCount,
      notesCount: t.stats.notesCount,
      hasStatsInMcqStats: !!mcqStats[t.id],
      mcqStatsKeys: Object.keys(mcqStats).length
    })));

    return processed;
  }, [topics, mcqStats, notesStats, userId, userTopicProgress]);

  // Filter and sort topics
  const filteredTopics = useMemo(() => {
    // Only show topics that have content for the user's selected exam
    let filtered = enrichedTopics.filter(topic => {
      const hasContent = topic.stats.mcqCount > 0 || topic.stats.notesCount > 0;
      if (!hasContent) return false;
      if (!searchQuery) return true;
      return topic.name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    switch (sortBy) {
      case 'content':
        filtered.sort((a, b) => (b.stats.mcqCount + b.stats.notesCount) - (a.stats.mcqCount + a.stats.notesCount));
        break;
      case 'progress':
        filtered.sort((a, b) => b.stats.progress - a.stats.progress);
        break;
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [enrichedTopics, searchQuery, sortBy]);

  const handleTopicAction = (topicId: string, action: 'mcq' | 'notes' | 'mixed') => {
    if (onTopicSelect) {
      onTopicSelect(topicId, action);
    } else {
      // Default navigation with exam context
      const examParam = selectedExamSlug ? `&exam=${selectedExamSlug}` : '';
      switch (action) {
        case 'mcq':
          navigate(`/practice?topic=${topicId}${examParam}`);
          break;
        case 'notes':
          navigate(`/notes?topic=${topicId}${examParam}`);
          break;
        case 'mixed':
          navigate(`/study/${topicId}`);
          break;
      }
    }
  };

  const resetTopicProgress = async (topicId: string) => {
    if (!userId) {
      toast({
        title: "Not logged in",
        description: "Please log in to reset progress",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: mcqs, error: mcqError } = await supabase
        .from("mcqs")
        .select("id")
        .eq("topic_id", topicId);

      if (mcqError) throw mcqError;

      const mcqIds = mcqs?.map(m => m.id) || [];

      if (mcqIds.length === 0) {
        toast({
          title: "No MCQs found",
          description: "This topic has no MCQs to reset",
        });
        return;
      }

      const { error: deleteError } = await supabase
        .from("attempts")
        .delete()
        .eq("user_id", userId)
        .in("mcq_id", mcqIds);

      if (deleteError) throw deleteError;

      toast({
        title: "Progress reset successfully",
        description: `Your progress for this topic has been cleared`,
      });

      queryClient.invalidateQueries({ queryKey: ["user-topic-progress"] });
    } catch (error) {
      console.error("Error resetting progress:", error);
      toast({
        title: "Reset failed",
        description: "Could not reset progress. Please try again.",
        variant: "destructive"
      });
    }
  };

  const TopicCard = ({ topic }: { topic: typeof enrichedTopics[0] }) => {
    const Icon = getTopicIcon(topic.name);
    const colorClass = getSpecialtyColor(topic.name);
    const hasContent = topic.stats.mcqCount > 0 || topic.stats.notesCount > 0;

    return (
      <Card className={`medical-card group hover:shadow-feature cursor-pointer ${!hasContent ? 'opacity-60' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className={`feature-icon bg-${colorClass}/10 text-${colorClass} group-hover:scale-110`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex items-center gap-2">
              {topic.stats.progress > 0 && (
                <>
                  <Badge variant="secondary" className="text-xs">
                    {topic.stats.progress}% done
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setResetTopicId(topic.id);
                    }}
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
          <CardTitle className="text-lg leading-tight">{topic.name}</CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Content Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <div className="text-sm">
                <div className="font-medium">{topic.stats.mcqCount}</div>
                <div className="text-xs text-muted-foreground">MCQs</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-accent" />
              <div className="text-sm">
                <div className="font-medium">{topic.stats.notesCount}</div>
                <div className="text-xs text-muted-foreground">Notes</div>
              </div>
            </div>
          </div>

          {/* Difficulty Distribution */}
          {topic.stats.mcqCount > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">MCQ Difficulty</div>
              <div className="flex gap-1 h-2">
                <div 
                  className="bg-medical-green rounded-sm"
                  style={{ width: `${(topic.stats.difficulty.easy / topic.stats.mcqCount) * 100}%` }}
                />
                <div 
                  className="bg-medical-orange rounded-sm"
                  style={{ width: `${(topic.stats.difficulty.medium / topic.stats.mcqCount) * 100}%` }}
                />
                <div 
                  className="bg-destructive rounded-sm"
                  style={{ width: `${(topic.stats.difficulty.hard / topic.stats.mcqCount) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Estimated Time */}
          {topic.stats.estimatedTime > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{topic.stats.estimatedTime} min reading</span>
            </div>
          )}

          {/* Progress */}
          {topic.stats.progress > 0 && (
            <div className="space-y-2">
              <Progress value={topic.stats.progress} className="h-2" />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => handleTopicAction(topic.id, 'mcq')}
              disabled={topic.stats.mcqCount === 0}
              className="flex-1 text-xs"
            >
              <Target className="h-3 w-3 mr-1" />
              Practice
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleTopicAction(topic.id, 'notes')}
              disabled={topic.stats.notesCount === 0}
              className="flex-1 text-xs"
            >
              <BookOpen className="h-3 w-3 mr-1" />
              Study
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Question Bank</h1>
            <p className="text-muted-foreground">
              Choose a medical specialty to start practicing
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {userId && <ExamSwitcher />}
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search medical topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortBy(sortBy === 'name' ? 'content' : sortBy === 'content' ? 'progress' : 'name')}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Sort: {sortBy === 'name' ? 'Name' : sortBy === 'content' ? 'Content' : 'Progress'}
            </Button>
          </div>
        </div>

        {/* Error States */}
        {mcqStatsError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load MCQ stats</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{mcqStatsError.message}</span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["topic-mcq-stats-filtered"] })}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {notesStatsError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load notes stats</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{notesStatsError.message}</span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["topic-notes-stats-filtered"] })}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {(mcqStatsLoading || notesStatsLoading || examContextLoading || isAutoSelecting) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        )}

        {/* Stats Summary */}
        {!mcqStatsLoading && !notesStatsLoading && !examContextLoading && !isAutoSelecting && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="medical-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Stethoscope className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Topics</span>
            </div>
            <div className="text-2xl font-bold">{filteredTopics.length}</div>
            <p className="text-xs text-muted-foreground">Available specialties</p>
          </div>
          
          <div className="medical-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">MCQs</span>
            </div>
            <div className="text-2xl font-bold">
              {filteredTopics.reduce((sum, t) => sum + t.stats.mcqCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Practice questions</p>
          </div>
          
          <div className="medical-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Notes</span>
            </div>
            <div className="text-2xl font-bold">
              {filteredTopics.reduce((sum, t) => sum + t.stats.notesCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Study materials</p>
          </div>
          
          <div className="medical-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Time</span>
            </div>
            <div className="text-2xl font-bold">
              {Math.round(filteredTopics.reduce((sum, t) => sum + t.stats.estimatedTime, 0) / 60)}h
            </div>
            <p className="text-xs text-muted-foreground">Estimated reading</p>
          </div>
        </div>
        )}
      </div>

      {/* Empty State for SMLE Users (or any exam with no content) */}
      {!mcqStatsLoading && !notesStatsLoading && selectedExamSlug && userId && 
       filteredTopics.every(t => t.stats.mcqCount === 0 && t.stats.notesCount === 0) && (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="feature-icon bg-primary/10 text-primary">
              <BookOpen className="h-8 w-8" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">No Content Available Yet</h3>
              <p className="text-muted-foreground mb-1">
                No MCQs or notes are available for the <strong>{selectedExamSlug.toUpperCase()}</strong> exam yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Content is being prepared. Check back soon!
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Loading Skeleton for Topic Cards */}
      {(mcqStatsLoading || notesStatsLoading) && (
        <div className={
          viewMode === 'grid' 
            ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            : "space-y-3"
        }>
          {[1,2,3,4,5,6,7,8].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-3">
                <Skeleton className="h-10 w-10 rounded-lg mb-2" />
                <Skeleton className="h-5 w-3/4" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
                <Skeleton className="h-2 w-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Topics Grid/List */}
      {!mcqStatsLoading && !notesStatsLoading && (
        <div className={
          viewMode === 'grid' 
            ? "grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            : "space-y-3"
        }>
          {filteredTopics.map((topic) => {
            const completion = topic.stats.mcqCount > 0 
              ? Math.round((topic.stats.progress / 100) * topic.stats.mcqCount) / topic.stats.mcqCount * 100
              : 0;
            
            const Icon = getTopicIcon(topic.name);

            return viewMode === 'grid' ? (
              <TopicGridCard
                key={topic.id}
                id={topic.id}
                name={topic.name}
                questionCount={topic.stats.mcqCount}
                completionPercentage={completion}
                icon={Icon.name}
              />
            ) : (
              <TopicCard key={topic.id} topic={topic} />
            );
          })}
        </div>
      )}

      {!mcqStatsLoading && !notesStatsLoading && filteredTopics.length === 0 && !selectedExamSlug && (
        <div className="medical-card p-12 text-center">
          <Search className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No topics found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search query or check back later for new content
          </p>
          <Button onClick={() => setSearchQuery("")}>
            Clear Search
          </Button>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={!!resetTopicId} onOpenChange={(open) => !open && setResetTopicId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Topic Progress?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all your attempt history for{" "}
              <strong>{topics.find(t => t.id === resetTopicId)?.name}</strong>.
              You will lose all progress and statistics for this topic. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (resetTopicId) {
                  resetTopicProgress(resetTopicId);
                  setResetTopicId(null);
                }
              }}
            >
              Reset Progress
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};